import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  closeComposerSafe,
  discardComposer,
  saveAndCloseComposer,
  saveDraft,
  sendComposer,
  updateComposer,
} from "../actions";
import {
  composer,
  composerDraftUid,
  composerError,
  composerLastSaved,
  composerSaving,
  composerSending,
  composerShowConfirm,
  defaultDomain,
} from "../state";
import { Icon } from "./icons";

const TITLES: Record<string, string> = {
  new: "New message",
  reply: "Reply",
  replyAll: "Reply all",
  forward: "Forward",
};

export function Composer(): JSX.Element | null {
  const draft = composer.value;
  const bodyRef = useRef<HTMLDivElement>(null);

  // Esc closes; Ctrl/Cmd+Enter sends.
  useEffect(() => {
    if (!draft) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeComposerSafe();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void sendComposer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft]);

  // Initialize contenteditable with draft body on mount / mode change.
  useEffect(() => {
    if (!draft || !bodyRef.current) return;
    const hasHtml = draft.body.includes("<");
    bodyRef.current.innerHTML = hasHtml
      ? draft.body
      : draft.body.replace(/\n/g, "<br>");
    if (draft.mode === "reply" || draft.mode === "replyAll") {
      bodyRef.current.focus();
    }
  }, [draft?.mode]);

  if (!draft) return null;

  const sending = composerSending.value;
  const saving = composerSaving.value;
  const err = composerError.value;
  const lastSaved = composerLastSaved.value;
  const draftUid = composerDraftUid.value;
  const showConfirm = composerShowConfirm.value;

  /** Human-readable save status for the indicator. */
  function saveStatus(): string {
    if (saving) return "Saving…";
    if (err && !lastSaved) return "Could not save";
    if (lastSaved) return draftUid ? "Saved" : "Saved as new";
    return "";
  }

  const saveLabel = saveStatus();

  function execCmd(cmd: string, value?: string): void {
    document.execCommand(cmd, false, value);
    if (bodyRef.current) {
      updateComposer({ body: bodyRef.current.innerHTML });
    }
  }

  function handleLinkInsert(): void {
    const url = window.prompt("URL:", "https://");
    if (url && url.trim()) {
      execCmd("createLink", url.trim());
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-0 sm:items-center sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeComposerSafe();
      }}
    >
      <div class="glass flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-t-xl shadow-2xl ring-1 ring-strong sm:rounded-xl">
        {/* header */}
        <div class="flex h-11 shrink-0 items-center gap-2 border-b border-strong bg-ink-2 px-4">
          <span class="text-sm font-semibold text-fg">{TITLES[draft.mode]}</span>
          <button class="icon-btn ml-auto" title="Close" onClick={() => closeComposerSafe()}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* fields */}
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <Field label="To">
            <RecipientInput
              value={draft.to}
              autofocus={draft.mode === "new" || draft.mode === "forward"}
              placeholder="recipient@example.com"
              onValue={(v) => updateComposer({ to: v })}
            />
            {!draft.showCc && (
              <button
                class="shrink-0 px-2 text-xs font-medium text-muted hover:text-fg"
                onClick={() => updateComposer({ showCc: true })}
              >
                Cc/Bcc
              </button>
            )}
          </Field>

          {draft.showCc && (
            <>
              <Field label="Cc">
                <RecipientInput
                  value={draft.cc}
                  placeholder="cc@example.com"
                  onValue={(v) => updateComposer({ cc: v })}
                />
              </Field>
              <Field label="Bcc">
                <RecipientInput
                  value={draft.bcc}
                  placeholder="bcc@example.com"
                  onValue={(v) => updateComposer({ bcc: v })}
                />
              </Field>
            </>
          )}

          <Field label="Subject">
            <input
              class="composer-input"
              value={draft.subject}
              placeholder="Subject"
              onInput={(e) => updateComposer({ subject: (e.target as HTMLInputElement).value })}
            />
          </Field>

          {/* Formatting toolbar */}
          <div class="flex flex-wrap items-center gap-0.5 border-b border-strong px-2 py-1">
            <button
              type="button"
              class="icon-btn text-xs font-bold px-1.5 h-7 w-7"
              title="Bold"
              onMouseDown={(e) => { e.preventDefault(); execCmd("bold"); }}
            >
              B
            </button>
            <button
              type="button"
              class="icon-btn text-xs italic px-1.5 h-7 w-7"
              title="Italic"
              onMouseDown={(e) => { e.preventDefault(); execCmd("italic"); }}
            >
              I
            </button>
            <button
              type="button"
              class="icon-btn text-xs underline px-1.5 h-7 w-7"
              title="Underline"
              onMouseDown={(e) => { e.preventDefault(); execCmd("underline"); }}
            >
              U
            </button>
            <span class="mx-1 h-4 w-px bg-ink-4" />
            <button
              type="button"
              class="icon-btn text-xs px-1.5 h-7"
              title="Horizontal rule"
              onMouseDown={(e) => { e.preventDefault(); execCmd("insertHorizontalRule"); }}
            >
              —
            </button>
            <button
              type="button"
              class="icon-btn text-xs px-1.5 h-7"
              title="Insert link"
              onMouseDown={(e) => { e.preventDefault(); handleLinkInsert(); }}
            >
              Link
            </button>
            <button
              type="button"
              class="icon-btn text-xs px-1.5 h-7"
              title="Bullet list"
              onMouseDown={(e) => { e.preventDefault(); execCmd("insertUnorderedList"); }}
            >
              • list
            </button>
            <button
              type="button"
              class="icon-btn text-xs px-1.5 h-7"
              title="Numbered list"
              onMouseDown={(e) => { e.preventDefault(); execCmd("insertOrderedList"); }}
            >
              1. list
            </button>
          </div>

          {/* Rich-text body */}
          <div
            ref={bodyRef}
            contentEditable={true}
            class="composer-input min-h-[160px] flex-1 overflow-y-auto px-4 py-3 text-sm leading-relaxed outline-none"
            onInput={() => {
              if (bodyRef.current) {
                updateComposer({ body: bodyRef.current.innerHTML });
              }
            }}
          />
        </div>

        {/* footer */}
        <div class="flex shrink-0 items-center gap-3 border-t border-strong bg-ink-2 px-4 py-3">
          <button
            class="btn-accent"
            disabled={sending}
            onClick={() => void sendComposer()}
          >
            {sending ? (
              <span class="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              <Icon name="send" size={16} />
            )}
            {sending ? "Sending…" : "Send"}
          </button>
          <button
            class="btn-outline text-sm"
            disabled={saving || sending}
            onClick={() => void saveDraft()}
            title="Save draft"
          >
            {saving ? (
              <span class="h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-accent" />
            ) : (
              "Save draft"
            )}
          </button>
          {saveLabel && (
            <span class="text-xs text-muted ml-1">{saveLabel}</span>
          )}
          {err && <span class="text-sm text-danger">{err}</span>}
          <span class="ml-auto hidden text-xs text-muted sm:block">
            {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}+Enter to send
          </span>
        </div>
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div
          class="fixed inset-0 z-60 flex items-center justify-center bg-scrim"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              composerShowConfirm.value = false;
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Discard changes?"
        >
          <div class="glass max-w-sm w-full rounded-xl p-6 shadow-2xl ring-1 ring-strong">
            <h3 class="text-base font-semibold text-fg mb-2">Discard changes?</h3>
            <p class="text-sm text-fg-dim mb-5">
              This message has unsaved changes.
            </p>
            <div class="flex gap-2 justify-end">
              <button
                class="btn-ghost text-sm"
                onClick={() => { void discardComposer(); }}
              >
                Discard
              </button>
              <button
                class="btn-accent text-sm"
                onClick={() => { void saveAndCloseComposer(); }}
              >
                Save &amp; Close
              </button>
              <button
                class="btn-ghost text-sm"
                onClick={() => { composerShowConfirm.value = false; }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecipientInput({
  value,
  autofocus,
  placeholder,
  onValue,
}: {
  value: string;
  autofocus?: boolean;
  placeholder?: string;
  onValue: (v: string) => void;
}): JSX.Element {
  const domain = defaultDomain.value;
  const [showSuggest, setShowSuggest] = useState(false);

  function handleInput(e: Event): void {
    const v = (e.currentTarget as HTMLInputElement).value;
    onValue(v);
    if (domain && v.endsWith("@")) {
      setShowSuggest(true);
    } else {
      setShowSuggest(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent): void {
    if (showSuggest && (e.key === "Tab" || e.key === "Enter") && domain) {
      e.preventDefault();
      onValue(value + domain);
      setShowSuggest(false);
    }
    if (e.key === "Escape") setShowSuggest(false);
  }

  function handleBlur(): void {
    setShowSuggest(false);
    if (domain && value.trim() && !value.includes("@") && !value.includes(",") && !value.includes(";")) {
      onValue(value.trim() + "@" + domain);
    }
  }

  return (
    <div class="relative flex-1 min-w-0">
      <input
        class="composer-input w-full"
        value={value}
        autofocus={autofocus}
        placeholder={placeholder}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
      {showSuggest && domain && (
        <div class="glass absolute left-0 top-full z-20 mt-0.5 rounded-lg border border-subtle shadow-lg">
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm text-fg-dim hover:bg-ink-3 w-full text-left rounded-lg"
            onMouseDown={(e) => {
              e.preventDefault(); // prevent blur
              onValue(value + domain);
              setShowSuggest(false);
            }}
          >
            <span class="text-accent">@</span>
            <span>{domain}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ComponentChildren }): JSX.Element {
  return (
    <div class="flex items-center gap-2 border-b border-strong px-4">
      <span class="w-14 shrink-0 text-xs font-medium text-muted">{label}</span>
      {children}
    </div>
  );
}
